
import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Card, Button, Form, FormGroup, Tabs, Tab,Dropdown } from 'react-bootstrap'
import { Editor } from '@tinymce/tinymce-react'
import { Checkbox, Radio } from '@mui/material'
import FeatherIcon from 'feather-icons-react'
import { SurveyResults } from 'survey-creator-react'
import { Model, StylesManager } from 'survey-core'
import PerfectScrollbar from 'react-perfect-scrollbar'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

const getTitle = (answer, field, currentLanguage) => {
  if (!answer || typeof answer[field] === 'undefined') {
    return null;  // Return null if field doesn't exist
  }

  const label =
    typeof answer[field] === 'object' && answer[field] !== null
      ? answer[field][currentLanguage] || answer[field].default
      : answer[field];

  return label;
};


const mockElement = (mockResponses, element) => {
  if (element.type === 'radiogroup') {
    // Randomly select one of the available choices
    const randomChoice = element.choices[Math.floor(Math.random() * element.choices.length)];
    mockResponses[element.name] = randomChoice.value;
  } else if (element.type === 'boolean') {
    // Randomly select true or false
    if (element.valueTrue) {
      mockResponses[element.name] = Math.random() > 0.5 ? element.valueTrue : element.valueFalse;
    } else {
      mockResponses[element.name] = Math.random() > 0.5;
    }
  } else if (element.type === 'comment') {
    // Add a mock comment for comment type
    mockResponses[element.name] = "This is a mock comment.";
  } else if (element.type === 'text') {
    if (element.inputType === 'datetime-local')
      mockResponses[element.name] = moment().format('YYYY-MM-DDTHH:mm');
    else
      mockResponses[element.name] = "This is a mock text.";
  }
  return mockResponses
}


const generateMockResponses = (surveyData,currentLanguage='default') => {
  let mockResponses = {};

  // Iterate through the survey structure
  surveyData.pages.forEach(page => {
    page.elements.forEach(element => {
      if (element.type === 'panel') {
        if(element?.elements){
          element.elements.forEach(subEle => {
            mockResponses = mockElement(mockResponses, subEle)
          })
        }
      } else {
        mockResponses = mockElement(mockResponses, element)
      }
    });
  });

  return mockResponses;
}

const objElement = (objResponses, element, replaceWithTitle = true) => {

  if (['boolean', 'radiogroup'].includes(element.type) && replaceWithTitle) {
    objResponses[element.name] = `<code>@${element.name}/title</code>`
  } else {
    objResponses[element.name] = `<code>@${element.name}</code>`
  }
  return objResponses
}

const generateObjResponses = (surveyData, replaceWithTitle,currentLanguage='default') => {
  let objResponses = {};

  // Iterate through the survey structure
  surveyData.pages.forEach(page => {
    page.elements.forEach(element => {
      if (element.type === 'panel') {
        if(element?.elements){
          element.elements.forEach(subEle => {
            objResponses = objElement(objResponses, subEle, replaceWithTitle)
          })
        }
      } else {
        objResponses = objElement(objResponses, element, replaceWithTitle)
      }
    });
  });

  return objResponses;
}

const elementResponse = (messageParts, element, userResponse, showTitle = true, replaceWithTitle,currentLanguage='default') => {
  if (element.type === 'radiogroup') {
    // Find the selected choice text

    const selectedChoice = element.choices.find(choice => choice.value === userResponse);
    if (selectedChoice) {
      if (showTitle) {
        messageParts.push(`<span class='title-label'>${element.summaryTemplateTitle || getTitle(element,'title',currentLanguage)} </span> ${getTitle(selectedChoice,'text',currentLanguage)}`);
      } else {
        messageParts.push(`${getTitle(selectedChoice,'text',currentLanguage)}`);
      }
    } else if (userResponse) {
      if (replaceWithTitle) {
        messageParts.push(`${userResponse}`);
      } else {
        messageParts.push(`<span class='title-label'>${element.summaryTemplateTitle || getTitle(element,'title',currentLanguage)} </span> ${userResponse}`);
      }
    }
  } else if (element.type === 'boolean') {
    // Handle boolean values
    if (element.valueTrue) {
      messageParts.push(`<span class='title-label'>${element?.summaryTemplateTitle || getTitle(element,'title',currentLanguage)} :</span> ${userResponse}`);
    } else if (userResponse === true) {
      messageParts.push(`${element?.summaryTemplateTitle || element.title} `);
    } else if (userResponse) {
      if (replaceWithTitle) {
        messageParts.push(`${userResponse}`);
      } else {
        messageParts.push(`<span class='title-label'>${element.summaryTemplateTitle || getTitle(element,'title',currentLanguage)}: </span> ${userResponse}`);
      }
    }
  } else if (element.type === 'text' && userResponse) {
    if (element.inputType === 'datetime-local') {
      messageParts.push(`<span class='title-label'>${element.summaryTemplateTitle || getTitle(element,'title',currentLanguage)}: </span> ${userResponse}`);
    } else {
      messageParts.push(`<span class='title-label'>${element.summaryTemplateTitle || getTitle(element,'title',currentLanguage)}: </span> ${userResponse}`);
    }
  } else if (element.type === 'comment' && userResponse) {
    messageParts.push(`<span class='title-label'>${element.summaryTemplateTitle || getTitle(element,'title',currentLanguage)}: </span> ${userResponse}`);
  }
  return messageParts
}

const processSurveyResponse = (surveyData, userResponses, replaceWithTitle,currentLanguage='default') => {
  let messageParts = [];
  let objParts = [];

  // Add Title (CC1: <Title>)
  //messageParts.push(`CC1: <b>${surveyData.title}</b><br/>`);

  // Iterate over the elements to build the message
  surveyData.pages.forEach(page => {
    page.elements.forEach(element => {
      const userResponse = userResponses[element.name];
      if (element.type === 'panel') {
        if(element?.elements){
          let subParts = []
          element.elements.forEach(subEle => {
            const subResponse = userResponses[subEle.name];
            subParts = elementResponse(subParts, subEle, subResponse, false, replaceWithTitle,currentLanguage)
          })
          messageParts.push(`<span class='title-label'>${element.title} </span> : ${subParts.join(' / ')}`);
        }
      } else {
        messageParts = elementResponse(messageParts, element, userResponse, true, replaceWithTitle,currentLanguage)
      }
    });
  });

  // Add Date of Onset (Static for example purposes)
  //messageParts.push(`<span class='title-label'>Date of onset:</span> 04-Oct-2024`);

  // Join all message parts into a final string
  const title = getTitle(surveyData,'title',currentLanguage);
  if(title)
    return `<b>${title}</b><br/>` + messageParts.join(', ')
  else 
    return messageParts.join(', ')
}



export const transferTemplate = (jsonSurvey, htmlTemplate, userResponse,currentLanguage='default') => {
  if(!htmlTemplate || htmlTemplate==''){
    return ''
  }
  // Helper function to recursively extract all elements from pages and panels
  const extractElements = (pageOrPanel) => {
    let elements = [];
    if (pageOrPanel.elements) {
      pageOrPanel.elements.forEach(element => {
        if (element.type === "panel" && element.elements) {
          // If it's a panel, recursively extract its elements
          elements = elements.concat(extractElements(element));
        } else {
          // Otherwise, it's a regular element, so we just add it
          elements.push(element);
        }
      });
    }
    return elements;
  };

  if(typeof htmlTemplate ==='object'){
    htmlTemplate = htmlTemplate[currentLanguage] || htmlTemplate['default']
  }
  // Loop through user responses
  Object.keys(userResponse).forEach(key => {
    // Standard placeholder replacement
    const placeholder = `<code>@${key}</code>`;
    const titlePlaceholder = `<code>@${key}/title</code>`;

    // Replace standard placeholder with actual value from userResponse
    htmlTemplate = htmlTemplate.replace(new RegExp(placeholder, 'g'), userResponse[key]);

    // Check if titlePlaceholder exists in the template
    if (htmlTemplate.includes(titlePlaceholder)) {
      // Extract all elements from the survey, including nested panels
      const allElements = jsonSurvey.pages.flatMap(page => extractElements(page));

      // Find the element with the matching key
      const surveyElement = allElements.find(element => element.name === key);
      const title = surveyElement ? (surveyElement.summaryTemplateTitle || getTitle(surveyElement,'title',currentLanguage)) : '';

      let replacementValue = userResponse[key] ? title : '';
      // If userResponse[key] is true, use the title; if false, leave empty
      if (surveyElement && surveyElement.type === 'radiogroup') {
        // Find the selected choice text

        const selectedChoice = surveyElement.choices.find(choice => choice.value === userResponse[key]);
        if (selectedChoice) {
          replacementValue = getTitle(selectedChoice,'text',currentLanguage)
        }
      }
      // Replace the titlePlaceholder with the title or empty string
      htmlTemplate = htmlTemplate.replace(new RegExp(titlePlaceholder, 'g'), replacementValue);
    }
  });

  htmlTemplate = htmlTemplate.replace(/<code>.*?<\/code>/g, '');
  return htmlTemplate;
};

export const getLangList = (jsonSurvey)=>{

  if(jsonSurvey.title && typeof jsonSurvey.title ==='object'){
    return Object.keys(jsonSurvey.title)
  }else{
    return ['default']
  }
}

const SummaryTemplate = ({ value, onChange, jsonSurvey = {} }) => {
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language

  const [preferLang,setPreferLang] = useState(currentLanguage)
  const mock = generateMockResponses(jsonSurvey);
  const [sampleValue, setSampleValue] = useState(transferTemplate(jsonSurvey, value, mock,preferLang))
  const [mockAnswer, setMockAnswer] = useState(mock)
  const [sampleAnswer, setSampleAnswer] = useState({})
  const [replaceWithTitle, setReplaceWithTitle] = useState(true)
  const [active,setActive] = useState('sampleResult')
  const survey = new Model(jsonSurvey)
  const langList = getLangList(jsonSurvey)

  const [editorValue,setEditorValue] = useState((value &&  typeof value ==='object')?(value[preferLang] || value['default']||''):value)


  const updateData = (string) => {
    // setEditorValue(string)
  }




  const onClickAutoGen = () => {

    const mock = generateMockResponses(jsonSurvey,preferLang)
    const obj = generateObjResponses(jsonSurvey, replaceWithTitle,preferLang)
    //const newValueMock = value + "" +processSurveyResponse(jsonSurvey,mock)
    const newValue = (value ||'') + "" + processSurveyResponse(jsonSurvey, obj, replaceWithTitle,preferLang)
    setMockAnswer(mock)
    setSampleAnswer(obj)
    //setSampleValue(newValueMock)

    survey.data = mock

    if (onChange) {
      if(langList.length>1){
        let newValueChange= {default:''}
        langList.map((lang)=>{
          newValueChange[lang] = processSurveyResponse(jsonSurvey, obj, replaceWithTitle,lang)
        })
        onChange(newValueChange)        
      }else{
        onChange({default:newValue})        
      }
    }
    setEditorValue(newValue)

    setSampleValue(transferTemplate(jsonSurvey, newValue, mock,preferLang))
  }

  const onClickTransfer = () => {
    setSampleValue(transferTemplate(jsonSurvey, value, mockAnswer,currentLanguage))
  }

  const renderEditor = () => {
    return (<Editor
      apiKey="ijcrie2wo48kbeqad77tg82er0plrv3lwcggmiaww55zd8s4"
      init={{
        height: 250,
        menubar: true,
        plugins: [
          'paste importcss',
          'searchreplace autolink autosave',
          'save directionality  visualblocks',
          'visualchars ',
          'lists textpattern',
          'noneditable  charmap  emoticons',
        ],
        toolbar: [
          'undo redo',
          'addDataSource',
          'formatselect',
          'bold italic forecolor backcolor',
          'alignleft aligncenter',
          'alignright alignjustify',
          'bullist numlist outdent indent',
          'removeformat',
        ].join(' | '),
        menu: {
          insert: { title: 'Insert', items: '' } // Clear the 'Insert' menu
        },
        content_style: `
                    .title-label,.symptom-label {
                        font-weight: bold;
                        color: #98b2e6;
                        padding: 2px;
                        border-radius: 5px;
                        display: inline-block;
                    }
                        body { font-family:Helvetica,Arial,sans-serif; font-size:14px }
                    `
        ,
        setup: (editor) => {
          editor.ui.registry.addMenuButton('addDataSource', {
            text: 'Datasource',
            fetch: (callback) => {
              // let items = []

              // let name = 'jack'
              // let item = {
              //     type: 'menuitem',
              //     text: name,
              //     onAction: () => {
              //         editor.insertContent(`<code>@${name}</code>`)
              //     },
              // }

              const items = [
                {
                  type: 'menuitem',
                  text: 'Datasource 1',
                  onAction: function () {
                    editor.insertContent('<p>Datasource 1 selected</p>');
                  }
                },
                {
                  type: 'menuitem',
                  text: 'Datasource 2',
                  onAction: function () {
                    editor.insertContent('<p>Datasource 2 selected</p>');
                  }
                },
                {
                  type: 'menuitem',
                  text: 'Datasource 3',
                  onAction: function () {
                    editor.insertContent('<p>Datasource 3 selected</p>');
                  }
                },
              ];

              callback(items)
            },
          })
        },
      }}
      value={editorValue}
      onEditorChange={(text) => {setEditorValue(text);onChange(langList.length>1?{...value,[preferLang]:text}:{default:text})}}
    />)
  }

  survey.data = generateMockResponses(jsonSurvey,currentLanguage)

  const switchLang = (lang)=>{
    setPreferLang(lang)    
    setEditorValue(value[lang]||'')
  }

  const renderSwitchLang = (localeList=[])=>{
    return (                 
    <Dropdown >
        <Dropdown.Toggle variant="light" id="dropdown-basic-lang" >
            Switch Locale : <i className="feather icon-flag mr-1" /> {preferLang}
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {localeList.map((label) => {
            return (
              <Dropdown.Item
                onClick={() => switchLang(label) }
                style={{ color: "#000" }}
                className="bg-white"
              >
                <i className="feather icon-flag mr-1" /> {label}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>)
  }

  return (
    <Card className='summary-template'>
      <Card.Header className='pt-0 pb-0 d-flex'>
        <Button className='ml-1 mr-1' onClick={onClickAutoGen}>Auto Generate</Button>

        Boolean/Radio type will replace
        <label>
          <Radio
            color="primary"
            value={true}
            checked={replaceWithTitle === true}
            onChange={(e) => setReplaceWithTitle(!replaceWithTitle)}
          />
          with Title
        </label>
        <label>
          <Radio
            color="primary"
            value={false}
            checked={replaceWithTitle === false}
            onChange={(e) => setReplaceWithTitle(!replaceWithTitle)}
          />
          with Yes/No
        </label>
        {langList.length>1 && renderSwitchLang(langList)}
      </Card.Header>

      <Card.Body>
        {renderEditor()}

        <Tabs
          id="controlled-tab-result"
          className="mb-3"
          onSelect={(key) => setActive(key)}
        >
          <Tab eventKey="sampleResult" title="Sample Summary Result">
            <div className='mt-1'>Sample Result : <Button onClick={onClickTransfer}>Transfer</Button></div>

            <div contentEditable="false" className='content-editable'>
              <div dangerouslySetInnerHTML={{ __html: sampleValue }} />
            </div>
            {/* <div className='mt-1'>Sample Json Answer :</div>
            {JSON.stringify(mockAnswer)}

            <div className='mt-1'>Sample Obj Answer :</div>
            {JSON.stringify(sampleAnswer)} */}

          </Tab>
          <Tab eventKey="tableResult" title="Table Result">
            {active === 'tableResult' && (<PerfectScrollbar style={{height:'300px'}}><SurveyResults survey={survey} locale={preferLang} /></PerfectScrollbar>)}
          </Tab>
        </Tabs>
      </Card.Body>
    </Card>
  )
}

export default SummaryTemplate